import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Field, Icon, Input } from '@/components/primitives';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { env } from '@/lib/env';

interface Errors {
  email?: string;
  password?: string;
  server?: string;
}

export function LoginPage() {
  const { user, signIn, loading, isMock } = useAuth();
  const navigate = useNavigate();
  const { vw } = useViewport();
  const stacked = vw < 900;

  const [email, setEmail] = useState(isMock ? env.mockAuthEmail : '');
  const [password, setPassword] = useState(isMock ? env.mockAuthPassword : '');
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bx-page-bg)',
        }}
      >
        <Loader2
          style={{
            width: 24,
            height: 24,
            color: 'var(--bx-text-muted)',
            animation: 'wfpSpin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;

  function validate(): Errors {
    const e: Errors = {};
    if (!email) e.email = 'Bắt buộc';
    else if (!/.+@.+\..+/.test(email)) e.email = 'Email không hợp lệ';
    if (!password) e.password = 'Bắt buộc';
    else if (password.length < 6) e.password = 'Tối thiểu 6 ký tự';
    return e;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const ve = validate();
    setErrors(ve);
    if (Object.keys(ve).length) return;
    setSubmitting(true);
    const { error } = await signIn(email, password);
    setSubmitting(false);
    if (error) {
      setErrors({ server: error });
      return;
    }
    navigate('/', { replace: true });
  }

  const valid = Object.keys(validate()).length === 0;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bx-page-bg)',
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
        overflow: 'auto',
      }}
    >
      {/* Left: form */}
      <div
        style={{
          display: 'flex',
          alignItems: stacked ? 'flex-start' : 'center',
          justifyContent: 'center',
          padding: stacked ? '32px 20px' : 24,
          background: '#fff',
          order: stacked ? 2 : 1,
        }}
      >
        <form
          onSubmit={submit}
          style={{
            width: '100%',
            maxWidth: 380,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: 'var(--bx-primary)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              W
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--bx-text-heading)' }}>
                Kế hoạch nhân sự
              </div>
              <div style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>
                v2 · Boxme vận hành nội bộ
              </div>
            </div>
          </div>

          <div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: 'var(--bx-text-heading)',
                margin: 0,
              }}
            >
              Đăng nhập
            </h1>
            <p
              style={{
                fontSize: 13,
                color: 'var(--bx-text-muted)',
                marginTop: 6,
                marginBottom: 0,
              }}
            >
              Dùng tài khoản{' '}
              <span style={{ fontFamily: 'var(--bx-font-mono)' }}>@boxme.tech</span>. SSO qua
              Google Workspace.
            </p>
          </div>

          {isMock && (
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: 12,
                background: 'var(--bx-warning-bg)',
                border: '1px solid #fde68a',
                borderRadius: 10,
                fontSize: 12,
                color: 'var(--bx-warning-fg)',
              }}
            >
              <Icon name="info" size={13} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700 }}>Đang chạy ở chế độ mock</div>
                <div style={{ marginTop: 2 }}>
                  Chưa kết nối Supabase. Dùng tài khoản demo:{' '}
                  <span style={{ fontFamily: 'var(--bx-font-mono)' }}>{env.mockAuthEmail}</span>{' '}
                  /{' '}
                  <span style={{ fontFamily: 'var(--bx-font-mono)' }}>
                    {env.mockAuthPassword}
                  </span>
                </div>
              </div>
            </div>
          )}

          <Field label="Email công việc" required error={errors.email}>
            <Input
              icon="mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ban@boxme.tech"
              error={!!errors.email}
              autoComplete="email"
            />
          </Field>
          <Field label="Mật khẩu" required error={errors.password}>
            <Input
              icon="lock"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              error={!!errors.password}
              autoComplete="current-password"
            />
          </Field>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: 12,
              color: 'var(--bx-text-muted)',
            }}
          >
            <label
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <input type="checkbox" style={{ accentColor: 'var(--bx-primary)' }} /> Giữ đăng
              nhập (8 giờ)
            </label>
            <a href="#" onClick={(e) => e.preventDefault()}>
              Quên mật khẩu
            </a>
          </div>

          {errors.server && (
            <div
              style={{
                padding: 10,
                background: 'var(--bx-error-bg)',
                color: 'var(--bx-error)',
                fontSize: 12.5,
                borderRadius: 8,
                border: '1px solid #fecaca',
              }}
            >
              {errors.server}
            </div>
          )}

          <Button type="submit" variant="primary" disabled={!valid || submitting} size="lg">
            {submitting ? 'Đang đăng nhập…' : 'Đăng nhập'}
          </Button>

          <div style={{ position: 'relative', textAlign: 'center', margin: '4px 0' }}>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 1,
                background: 'var(--bx-border-muted)',
              }}
            />
            <span
              style={{
                position: 'relative',
                background: '#fff',
                padding: '0 10px',
                fontSize: 11,
                color: 'var(--bx-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              hoặc
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            icon="globe"
            onClick={() => signIn(env.mockAuthEmail, env.mockAuthPassword)}
          >
            Tiếp tục với Google Workspace
          </Button>
          <div style={{ fontSize: 11, color: 'var(--bx-text-muted)', textAlign: 'center' }}>
            Cần cấp quyền? Liên hệ quản lý vận hành theo quốc gia.
          </div>
        </form>
      </div>

      {/* Right: brand panel */}
      <div
        style={{
          background: 'linear-gradient(180deg, #f7f8ff 0%, #ececec 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: stacked ? '32px 20px 24px' : 40,
          position: 'relative',
          order: stacked ? 1 : 2,
          minHeight: stacked ? 'auto' : '100vh',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {['VN', 'TH', 'PH'].map((c) => (
            <span
              key={c}
              style={{
                fontFamily: 'var(--bx-font-mono)',
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 8px',
                background: '#fff',
                border: '1px solid var(--bx-border-muted)',
                borderRadius: 9999,
                color: 'var(--bx-text-body)',
              }}
            >
              {c}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--bx-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 700,
            }}
          >
            Phiên bản v2 có gì
          </div>
          <h2
            style={{
              fontSize: stacked ? 22 : 28,
              fontWeight: 700,
              color: 'var(--bx-text-heading)',
              margin: 0,
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            Forecast — Phê duyệt — Báo cáo,
            <br /> tất cả trong một workspace.
          </h2>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              fontSize: 13.5,
              color: 'var(--bx-text-dark)',
            }}
          >
            {(
              [
                ['rocket', 'Pipeline dự báo + WLU tính tự động theo 90 ngày lịch sử'],
                ['shield', 'Audit trail đầy đủ, không sửa được — kiểm toán an toàn'],
                ['layers', 'Tham số mùa + sự kiện versioned — auto-tune sẵn sàng post-MVP'],
              ] as const
            ).map(([icon, text]) => (
              <li
                key={text}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid var(--bx-border-muted)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--bx-primary)',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={icon} size={12} />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>
          © 2026 Boxme Vận hành · Mọi truy cập đều được audit log
        </div>
      </div>
    </div>
  );
}
